import {useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'

import {MemberListSchemaResponse, MemberResponse, MemberSchemaResponse, Responses} from '../domain'
import {useMember} from '../hook'

import {toast} from 'react-toastify'
import useRequestParams from 'src/feature/bite/hook/request'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import {useAuthUser} from 'react-auth-kit'

const endPoints = {
  Segment: 'GetMemberSegments',
  Address: 'GetMemberAddresses',
  Consent: 'GetMemberConcents',
  Transaction: 'GetMemberTransactions',
  PushEngagement: 'GetMemberEngagements',
  EmailEngagement: 'GetMemberEngagements',
  SmsEngagement: 'GetMemberEngagements',
}

const typeOfFilterKey = {
  Segment: 'CreatedDate', // One Page
  Address: 'CreatedDate', // One Page
  Consent: 'CreatedDate', // One Page
  Transaction: 'Date',
  PushEngagement: 'CreatedOn',
  EmailEngagement: 'CreatedOn',
  SmsEngagement: 'CreatedOn',
}

export function useGetList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listFilter, listFilterSet, listSet} = useMember()

  const userID = auth()?.id

  return useQuery(
    ['members', listFilter],
    async () => {
      const Response = await fetch(`${API}/Member/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const parse = MemberListSchemaResponse.parse(Data)

      listSet(parse.response)
      listFilterSet({
        ...listFilter,
        paging: {
          currentPageNumber: parse.response.paging.currentPageNumber,
          pageSize: parse.response.paging.pageSize,
        },
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetail(memberId: string) {
  const auth = useAuthUser()
  const router = useNavigate()
  const {detail, detailSet} = useMember()
  const {headers, errorMessage, signOut} = useRequestParams()
  const userID = auth()?.id

  return useQuery(
    ['MemberDetail', detail?.memberId],
    async () => {
      if (detail?.memberId) return true

      const Response = await fetch(`${API}/Member/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          memberId,
          storeId: '',
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const response: MemberResponse = await Response.json()

      const parse = MemberSchemaResponse.parse(response)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!parse.response?.member) {
        router('/error/member')
        return true
      }

      detailSet(parse.response.member)
      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log('error', error)
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetTabDetail(memberId: string) {
  const {detailTab, tabName, updateDetail, listFilterMarketing} = useMember()
  const {headers, errorMessage, signOut} = useRequestParams()

  const apiEndPoint = endPoints[tabName]
  const filterKey = typeOfFilterKey[tabName]

  const defaultPagination = {currentPageNumber: 1, pageSize: 5}

  const paging =
    detailTab && detailTab[tabName]?.paging ? detailTab[tabName]?.paging : defaultPagination

  const filter = {
    filterList: listFilterMarketing.filterList,
    orderList: [{type: 2, key: filterKey}],
    paging: {currentPageNumber: paging.currentPageNumber, pageSize: paging.pageSize},
  }

  const timeZoneOffSet = getTimeZone()

  let engagementIndex: number, engagementName: string

  if (tabName === 'SmsEngagement') {
    engagementIndex = 1
    engagementName = 'textMessages'
  }
  if (tabName === 'PushEngagement') {
    engagementIndex = 3
    engagementName = 'pushes'
  }
  if (tabName === 'EmailEngagement') {
    engagementIndex = 2
    engagementName = 'emails'
  }

  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['MemberDetailTab', tabName, filter],
    async () => {
      if (tabName == null || tabName === 'Overview' || tabName === 'Transaction') return true

      const Response = await fetch(`${API}/Member/${apiEndPoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          memberId,
          userID,
          timeZoneOffSet,
          language: headers.Language,
          ...filter,
          ...(engagementIndex && {engagementType: engagementIndex}),
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const ResponseSchema = Responses[tabName]
      const safeParse = ResponseSchema.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(Data)

      if (['SmsEngagement', 'PushEngagement', 'EmailEngagement'].includes(tabName)) {
        updateDetail(
          {
            dataList: parse.response[engagementName],
            paging: parse.response.listPaging,
          },
          tabName
        )
      } else {
        updateDetail(parse.response, tabName)
      }

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error: any) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useRenewMemberTransactions(memberId: string) {
  const {headers, signOut} = useRequestParams()
  const {detailTabSet, listFilterTrx} = useMember()
  const auth = useAuthUser()
  const userID = auth()?.id

  const timeZoneOffSet = getTimeZone()

  const {data, isLoading, error, refetch} = useQuery(
    ['renewMemberTransactions', memberId, listFilterTrx],
    async () => {
      listFilterTrx.filterList = listFilterTrx.filterList.filter((item) => item.value?.[0] !== '')

      const response = await fetch(`${API}/Member/GetMemberTransactions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilterTrx,
          language: headers.Language,
          userID,
          timeZoneOffSet,
          memberId,
        }),
      })

      if (response.status === 401) {
        signOut()
      }

      const data = await response.json()
      const ResponseSchema = Responses['Transaction']
      const safeParse = ResponseSchema.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(data)
      detailTabSet((prev) => ({
        ...prev,
        Transaction: parse.response,
      }))
      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error('Error fetching transactions:', error) // Informative error handling
      },
      // Refetch data automatically when the 'handleSubmit' function is called
      refetchOnWindowFocus: false, // Avoid unnecessary refetches (optional)
      refetchInterval: 0, // Disable automatic refetching (optional)
    }
  )

  // Call refetch within 'handleSubmit' to manually trigger data refresh
  const handleRefresh = () => {
    refetch()
  }

  return {data, isLoading, error, handleRefresh}
}
